import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from '../hooks';
import { HistoryCard } from './HistoryCard';
import { Pagination, SkeletonCard, EmptyState } from '@/shared/components';
import { usePagination } from '@/shared/hooks';
import type { HistoryItem } from '../types';
import { IconImage, IconX } from '@/app/components/icons';

const PAGE_SIZE = 12;

interface HistoryGridProps {
  readonly onSelect: (item: HistoryItem) => void;
}

export function HistoryGrid({ onSelect }: HistoryGridProps) {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);

  const { data, isLoading, isError } = useHistory(PAGE_SIZE, 0, statusFilter);

  const pagination = usePagination(PAGE_SIZE, data?.total);

  const handleFilterChange = (s: string) => {
    setStatusFilter(s === 'all' ? undefined : s);
    pagination.reset();
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Status filter — always visible */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {(['all', 'completed', 'failed', 'cancelled'] as const).map((s) => {
          const isActive = s === 'all' ? !statusFilter : statusFilter === s;
          return (
            <button
              key={s}
              type="button"
              onClick={() => handleFilterChange(s)}
              className="shrink-0 rounded-full px-3 py-1.5 text-sm font-medium transition-all duration-150 active:scale-[0.96]"
              style={{
                backgroundColor: isActive
                  ? 'var(--tg-theme-button-color)'
                  : 'var(--tg-theme-secondary-bg-color)',
                color: isActive
                  ? 'var(--tg-theme-button-text-color)'
                  : 'var(--tg-theme-text-color)',
                border: isActive
                  ? '1px solid transparent'
                  : '1px solid color-mix(in srgb, var(--tg-theme-hint-color) 20%, transparent)',
              }}
            >
              {t(`history.filter.${s}`)}
            </button>
          );
        })}
      </div>

      {/* Content area */}
      {isLoading ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      ) : isError ? (
        <EmptyState
          icon={<IconX className="h-7 w-7 text-tg-destructive" />}
          title={t('history.loadError')}
          description={t('history.loadErrorDesc')}
        />
      ) : !data || !data.items || data.items.length === 0 ? (
        <EmptyState
          icon={<IconImage className="h-8 w-8" />}
          title={t('history.emptyTitle')}
          description={t('history.emptyDesc')}
        />
      ) : (
        <>
          {/* Grid */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {data.items.map((item) => (
              <HistoryCard key={item.id} item={item} onClick={() => onSelect(item)} />
            ))}
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            onPageChange={pagination.goToPage}
          />
        </>
      )}
    </div>
  );
}
